import React from 'react';
import type {AIAnalysis} from '../types';
import {formatPrice} from '../utils/patternTime';
import {calculateProfitLossRatio} from '../utils/tradeDrafts';
import {validatePriceRelation} from '../utils/tradeDirection';
import {SignalDirectionSplitButton} from './SignalDirectionSplitButton';

interface AiAnalysisReportProps {
    aiData: AIAnalysis;
    holdingPeriodText?: string;
    onGoTrade?: (direction: 'FORWARD' | 'REVERSE') => void;
    quickTradeEnabled?: boolean;
    validationError?: string | null;
    expiryReason?: string | null;
    signalDirection?: 1 | -1 | 0;
    signalConfidence?: number;
}


const riskLevelMap: Record<string, { label: string; color: string; width: string }> = {
    HIGH: {label: '高', color: 'var(--pt-bear)', width: '75%'},
    MEDIUM: {label: '中', color: 'var(--pt-amber)', width: '48%'},
    LOW: {label: '低', color: 'var(--pt-bull)', width: '25%'},
};

export const AiAnalysisReport: React.FC<AiAnalysisReportProps> = ({
                                                                      aiData,
                                                                      holdingPeriodText,
                                                                      onGoTrade,
                                                                      quickTradeEnabled = false,
                                                                      validationError,
                                                                      expiryReason,
                                                                      signalDirection,
                                                                      signalConfidence,
                                                                  }) => {
    const risk = riskLevelMap[aiData.riskLevel] ?? riskLevelMap.MEDIUM;
    const profitLossRatio = calculateProfitLossRatio(
        aiData.entryPrice ?? 0, aiData.targetPrice ?? 0, aiData.stopLossPrice ?? 0,
    );
    // 中性信号（direction=0）时从AI价格关系推断方向，使按钮展示与形态信号一致的样式
    const effectiveDirection: 1 | -1 | 0 = (0 !== signalDirection || null == aiData.entryPrice
        || null == aiData.targetPrice || null == aiData.stopLossPrice)
        ? (signalDirection ?? 0)
        : validatePriceRelation('long', aiData.entryPrice, aiData.targetPrice, aiData.stopLossPrice) ? 1
            : validatePriceRelation('short', aiData.entryPrice, aiData.targetPrice, aiData.stopLossPrice) ? -1
                : 0;

    return (
        <div className="pc-ai-report">
            {validationError && <div className="ai-qt-error">{validationError}</div>}
            {expiryReason && <div className="ai-qt-error">信号已过期：{expiryReason}</div>}
            <div className="metric-row" style={{marginTop: 4}}>
                <div className="metric-box">
                    <div className="metric-label">入场参考</div>
                    <div className="metric-val blue">${formatPrice(aiData.entryPrice)}</div>
                </div>
                <div className="metric-box">
                    <div className="metric-label">目标位</div>
                    <div className="metric-val bull">${formatPrice(aiData.targetPrice)}</div>
                </div>
                <div className="metric-box">
                    <div className="metric-label">止损位</div>
                    <div className="metric-val bear">${formatPrice(aiData.stopLossPrice)}</div>
                </div>
                <div className="metric-box">
                    <div className="metric-label">投入回报比</div>
                    <div className="metric-val amber">1:{profitLossRatio || '--'}</div>
                </div>
            </div>

            {holdingPeriodText && (
                <div style={{
                    marginTop: 4,
                    fontSize: 10,
                    color: 'var(--pt-text-dim)',
                    fontFamily: 'var(--pt-font-mono)'
                }}>
                    {holdingPeriodText}
                </div>
            )}

            <div className="risk-bar-wrap" style={{marginTop: 6}}>
                <div className="risk-label-row">
                    <span style={{fontSize: 10, fontFamily: 'var(--pt-font-mono)', color: 'var(--pt-text-dim)'}}>
                        风险评级
                    </span>
                    <span style={{fontSize: 10, fontFamily: 'var(--pt-font-mono)', fontWeight: 600, color: risk.color}}>
                        {risk.label}
                    </span>
                </div>
                <div className="risk-track">
                    <div className="risk-fill" style={{width: risk.width}}/>
                </div>
            </div>

            <div className="ai-reasoning" style={{marginTop: 6}}>
                <div className="ai-reasoning-title">
                    <span style={{color: 'var(--pt-amber)'}}>&#9674;</span> AI 解读
                </div>
                <div className="ai-reasoning-body">{aiData.reasoning}</div>
            </div>

            {aiData.signals.length > 0 && (
                <div className="signal-tag-row" style={{marginTop: 6}}>
                    {aiData.signals.map((s, i) => (
                        <span key={i} className="signal-tag active-amber">{s}</span>
                    ))}
                </div>
            )}

            {onGoTrade && (
                <div style={{marginTop: 8}}>
                    <SignalDirectionSplitButton
                        aiDirection={effectiveDirection}
                        confidence={signalConfidence ?? 50}
                        selected={null}
                        onSelect={(key) => {
                            onGoTrade('FORWARD' === key ? 'FORWARD' : 'REVERSE');
                        }}
                        neutral={false}
                    />
                </div>
            )}
        </div>
    );
};
